import type { AttentionKind } from './types.js'

/**
 * Every literal this screen uses.
 *
 * 9.1 says its one action is *"Open anything in the attention panel"*, so the
 * only thing here worth naming is where each kind of row opens — and that is a
 * decision about the product rather than a string, which is why it is written
 * down rather than assembled at the call site.
 */
export const ATTENTION_DESTINATION: Record<AttentionKind, string> = {
  /** An invitation nobody finished. The provider record, where it can be resent. */
  onboarding_awaiting_review: '/providers',
  provider_never_signed_in: '/providers',
  invitation_undelivered: '/providers',
  /** A refused payment with the time still held — the money, not the booking. */
  payment_failed: '/payments',
  orphan_payment: '/payments',
  /** A category that has filled up. Its limits live on Settings' categories. */
  category_full: '/providers',
  /**
   * Somebody at the missed-appointment threshold — decision 4.8, answered on
   * 2026-09-21 as **review**: the platform raises it and a person decides.
   *
   * **Their record**, which is where the deciding happens. This pointed at
   * the bookings table searched for their name, because when it was written
   * there was no members table to open — the row named a person the desk had
   * no screen for. There is one now, so the row opens it: their appointments
   * are on it, and so is the Suspend button, which names those appointments
   * and asks what happens to them before it does anything.
   */
  no_show_threshold: '/members',
}

/**
 * Six kinds, and the derived type is why that is known.
 *
 * The first version of this map also listed the four moderation kinds — a comment
 * removed, somebody muted — because they are in the same `kind` enum elsewhere in
 * the admin surface. They are not in *this* one, and `Record<AttentionKind, …>`
 * said so before the screen was ever rendered.
 *
 * Which is the argument for deriving types from the document rather than writing
 * them out: a hand-copied union would have accepted all ten and the four extra
 * entries would have sat here forever, pointing at a screen no attention row will
 * ever open.
 */

/**
 * The seven days the chart draws, whatever the API sends.
 *
 * A quiet day is a hairline rather than a gap — `BarChart` draws that — but only
 * if the day is in the array at all. A week with a missing Wednesday would draw
 * six columns and read as a shorter week.
 *
 * **This was declared and nothing used it.** The chart drew whatever came back,
 * so the sentence above described an intention rather than the screen. The
 * server builds the week with `generate_series` and so does send seven, which
 * is why nobody noticed and why it would have stayed unnoticed: the day it
 * sends six is the day the chart quietly lies about the week.
 */
export const DAYS_IN_A_WEEK = 7

/**
 * The board's own words on this frame.
 *
 * Its subtitle is *"Vancouver and Toronto · today"* — the two cities the mockup
 * ran in. The desk serves whatever cities exist, so it says the true version of
 * the same thing rather than naming two that may not be the two.
 */
export const SUBTITLE = 'Today, across every city'

export const CHART_TITLE = 'Bookings this week'
/**
 * What the seven columns actually are.
 *
 * The board's caption is *"Mon to Sun"* and the server does not build a
 * calendar week — it builds the **seven days ending today**, which on a
 * Monday reads Tue, Wed, Thu, Fri, Sat, Sun, Mon under a heading promising
 * Monday to Sunday. Seen the first day the desk was opened with data in it.
 *
 * The trailing week is the right thing to send: a calendar week shows one
 * column on a Monday. So the caption says what is drawn.
 */
export const CHART_RANGE = 'The last seven days, ending today'

/** The board's heading. This said "Wants attention", which is not English. */
export const WANTS_ATTENTION = 'Needs your attention'

/**
 * The month, in the order somebody would ask about it.
 *
 * Four figures for today are the board's; these six are the client's, asked
 * for on 2026-09-21. Kept in one list so the strip is a list of facts rather
 * than six hand-placed cards, and so adding a seventh is one line.
 *
 * `active_providers` is deliberately *"Professionals a client can find"* and
 * not "live providers": the Providers table says **Live** about anybody who
 * is not paused, and this counts the narrower thing — on the directory, with
 * something bookable and times open. The two numbers disagree on purpose and
 * the labels say why.
 */
export const THIS_MONTH_TITLE = 'The platform so far'
export const THIS_MONTH_RANGE = 'Totals, and this calendar month'

export const MONTHLY_FIGURES = [
  { key: 'total_users', label: 'Members' },
  { key: 'active_providers', label: 'Professionals a client can find' },
  { key: 'bookings_this_month', label: 'Bookings this month' },
  { key: 'upcoming_events', label: 'Events coming up' },
  { key: 'upcoming_classes', label: 'Classes coming up' },
] as const
